/**
 * /onboarding — onboard people and clean up duplicate accounts.
 *
 * Standardised across tenants and gated by the ONBOARDING permission. Works with or
 * without the employee module because it talks to the authentication module's shared
 * onboarding service directly:
 *  - Onboard by email / phone, optionally issuing a username + password, and deliver
 *    the credentials by email/SMS (even username/password users get them delivered —
 *    Paradise Lakes staff prefer username/password but we still send + still allow
 *    email/phone login).
 *  - Lookup-before-create on the backend means re-onboarding an existing person links
 *    to their account instead of creating a duplicate.
 *  - Surfaces "account fragmentation" (duplicate accounts sharing an email/phone) for a
 *    human to merge.
 */
import React, { useState } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Wrap,
    WrapItem,
    SimpleGrid,
    Card,
    CardHeader,
    CardBody,
    Badge,
    Button,
    Input,
    Select,
    Checkbox,
    FormControl,
    FormLabel,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Code,
    Divider,
    Tooltip,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    useColorMode,
    useToast,
    useDisclosure,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
} from "@chakra-ui/react";
import { FiUserPlus, FiAlertTriangle, FiRefreshCw } from "react-icons/fi";
import { useQuery, useMutation } from "@apollo/client";
import { getColor } from "../../brandConfig";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import {
    ONBOARD_CLIENT,
    RESET_CLIENT_CREDENTIALS,
    POTENTIAL_DUPLICATE_ACCOUNTS,
    MERGE_CLIENT_ACCOUNTS,
    ONBOARDED_CLIENTS,
} from "../authentication";

const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleString() : "—");

const OnboardingPage: React.FC = () => {
    const { colorMode } = useColorMode();
    const toast = useToast();

    const bgMain = getColor("background.main", colorMode);
    const cardGradientBg = getColor("background.cardGradient", colorMode);
    const cardBorder = getColor("border.darkCard", colorMode);
    const textPrimary = getColor(colorMode === "light" ? "text.primary" : "text.primaryDark", colorMode);
    const textSecondary = getColor(colorMode === "light" ? "text.secondary" : "text.secondaryDark", colorMode);
    const textMuted = getColor(colorMode === "light" ? "text.muted" : "text.mutedDark", colorMode);

    // ─── Onboard form ─────────────────────────────────────────────────────────
    const [fName, setFName] = useState("");
    const [lName, setLName] = useState("");
    const [email, setEmail] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [issueCredentials, setIssueCredentials] = useState(true);
    const [sendVia, setSendVia] = useState("auto");
    const [result, setResult] = useState<any | null>(null);

    const [onboardClient, { loading: onboarding }] = useMutation(ONBOARD_CLIENT);
    const [resetCreds] = useMutation(RESET_CLIENT_CREDENTIALS);
    const [mergeAccounts, { loading: merging }] = useMutation(MERGE_CLIENT_ACCOUNTS);

    const {
        data: dupData,
        loading: dupLoading,
        refetch: refetchDuplicates,
    } = useQuery(POTENTIAL_DUPLICATE_ACCOUNTS, { fetchPolicy: "cache-and-network" });
    const clusters: any[] = dupData?.potentialDuplicateAccounts || [];

    const { data: onbData, refetch: refetchOnboarded } = useQuery(ONBOARDED_CLIENTS, { fetchPolicy: "cache-and-network" });
    const onboarded: any[] = onbData?.onboardedClients || [];

    const canSubmit = (email.trim() || phoneNumber.trim()) && !onboarding;

    const handleOnboard = async () => {
        try {
            const { data } = await onboardClient({
                variables: {
                    input: {
                        fName: fName.trim() || undefined,
                        lName: lName.trim() || undefined,
                        email: email.trim() || undefined,
                        phoneNumber: phoneNumber.trim() || undefined,
                        issueCredentials,
                        sendVia,
                    },
                },
            });
            setResult(data.onboardClient);
            toast({
                title: data.onboardClient.created ? "Account created" : "Existing account used",
                description: data.onboardClient.matchedExisting
                    ? "Matched an existing person — no duplicate created."
                    : "New account onboarded.",
                status: "success",
                duration: 4000,
            });
            refetchDuplicates();
            refetchOnboarded();
        } catch (err: any) {
            toast({ title: "Onboarding failed", description: err.message, status: "error", duration: 6000 });
        }
    };

    const handleReset = async (clientId: string) => {
        try {
            const { data } = await resetCreds({ variables: { clientId, sendVia: "auto" } });
            setResult(data.resetClientCredentials);
            toast({ title: "Credentials reset & sent", status: "success", duration: 4000 });
        } catch (err: any) {
            toast({ title: "Reset failed", description: err.message, status: "error", duration: 6000 });
        }
    };

    // ─── Merge confirm ──────────────────────────────────────────────────────
    const mergeDialog = useDisclosure();
    const cancelRef = React.useRef<HTMLButtonElement>(null);
    const [mergePlan, setMergePlan] = useState<{ keepId: string; mergeIds: string[]; label: string } | null>(null);

    const confirmMerge = async () => {
        if (!mergePlan) return;
        try {
            await mergeAccounts({ variables: { keepClientId: mergePlan.keepId, mergeClientIds: mergePlan.mergeIds } });
            toast({ title: "Accounts merged", status: "success", duration: 4000 });
            mergeDialog.onClose();
            setMergePlan(null);
            refetchDuplicates();
            refetchOnboarded();
        } catch (err: any) {
            toast({ title: "Merge failed", description: err.message, status: "error", duration: 6000 });
        }
    };

    return (
        <Box minH="100vh" bg={bgMain} display="flex" flexDirection="column">
            <NavbarWithCallToAction />
            <Container maxW="container.lg" py={{ base: 6, md: 10 }} flex="1">
                <VStack align="stretch" spacing={6}>
                    <Box>
                        <HStack spacing={3} mb={1}>
                            <Box as={FiUserPlus} color={textPrimary} fontSize="2xl" aria-hidden />
                            <Heading size="lg" color={textPrimary}>Onboarding</Heading>
                        </HStack>
                        <Text color={textSecondary}>
                            Onboard people by email or phone and send them sign-in details. We never create a
                            duplicate — if someone already has an account, this links to it.
                        </Text>
                    </Box>

                    {/* Onboard form */}
                    <Card bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px">
                        <CardHeader pb={2}>
                            <Heading size="sm" color={textPrimary}>Onboard a person</Heading>
                        </CardHeader>
                        <CardBody pt={0}>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                                <FormControl>
                                    <FormLabel fontSize="sm" color={textMuted}>First name</FormLabel>
                                    <Input value={fName} onChange={(e) => setFName(e.target.value)} aria-label="First name" />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm" color={textMuted}>Last name</FormLabel>
                                    <Input value={lName} onChange={(e) => setLName(e.target.value)} aria-label="Last name" />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm" color={textMuted}>Email</FormLabel>
                                    <Input type="email" placeholder="person@example.com" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email" />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm" color={textMuted}>Phone</FormLabel>
                                    <Input type="tel" placeholder="+61 400 000 000" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} aria-label="Phone" />
                                </FormControl>
                            </SimpleGrid>

                            <Wrap spacing={4} align="center" mb={4}>
                                <WrapItem>
                                    <Tooltip
                                        hasArrow openDelay={300} placement="top" whiteSpace="pre-line" shouldWrapChildren
                                        label={"When ticked: if this person doesn't already have a password, we generate a username + temporary password for them and include it in the message we send.\n\nWhen unticked: we only create/find the account and (optionally) send a sign-in link — no password is issued.\n\nEither way they can still sign in by email or phone code."}
                                    >
                                        <Checkbox isChecked={issueCredentials} onChange={(e) => setIssueCredentials(e.target.checked)}>
                                            Issue username &amp; password
                                        </Checkbox>
                                    </Tooltip>
                                </WrapItem>
                                <WrapItem>
                                    <FormControl>
                                        <HStack>
                                            <FormLabel fontSize="sm" color={textMuted} mb={0}>Send via</FormLabel>
                                            <Tooltip
                                                hasArrow openDelay={300} placement="top" whiteSpace="pre-line" shouldWrapChildren
                                                label={"How the sign-in details are delivered to the person:\n\n  • Auto — emails them if you gave an email AND texts them if you gave a phone\n  • Email only — sends just to the email\n  • SMS only — texts just to the phone\n  • Both — forces email + SMS\n  • Don't send — creates the account but sends nothing (you'll read the details off this screen and pass them on yourself)"}
                                            >
                                                <Select value={sendVia} onChange={(e) => setSendVia(e.target.value)} size="sm" maxW="160px" aria-label="Send via">
                                                    <option value="auto">Auto (email + SMS)</option>
                                                    <option value="email">Email only</option>
                                                    <option value="sms">SMS only</option>
                                                    <option value="both">Both</option>
                                                    <option value="none">Don't send</option>
                                                </Select>
                                            </Tooltip>
                                        </HStack>
                                    </FormControl>
                                </WrapItem>
                            </Wrap>

                            <Tooltip
                                hasArrow openDelay={300} placement="top" whiteSpace="pre-line" shouldWrapChildren
                                label={"Onboard this person.\n\nWhat happens when you click:\n  1. We look up existing accounts by the email AND phone you entered — so we never create a duplicate\n  2. If a match is found, we link the new details onto that account; if not, we create a new one\n  3. If “Issue username & password” is on and they don't have one yet, we generate them\n  4. We deliver the sign-in details using the “Send via” choice\n  5. The username + temporary password appear below (once)"}
                            >
                                <Button
                                    leftIcon={<FiUserPlus />}
                                    colorScheme="blue"
                                    onClick={handleOnboard}
                                    isLoading={onboarding}
                                    isDisabled={!canSubmit}
                                >
                                    Onboard
                                </Button>
                            </Tooltip>

                            {result && (
                                <Alert status="success" mt={4} borderRadius="md" flexDirection="column" alignItems="flex-start">
                                    <HStack mb={2}><AlertIcon /><AlertTitle>Sign-in details</AlertTitle></HStack>
                                    <AlertDescription>
                                        <VStack align="start" spacing={1}>
                                            {result.username && <Text>Username: <Code>{result.username}</Code></Text>}
                                            {result.temporaryPassword
                                                ? <Text>Temporary password: <Code>{result.temporaryPassword}</Code> (shown once)</Text>
                                                : <Text color={textMuted}>This account already had a password — it was not changed.</Text>}
                                            <Text color={textMuted} fontSize="sm">
                                                Delivered via: {result.deliveredVia?.length ? result.deliveredVia.join(", ") : "not sent"}
                                            </Text>
                                        </VStack>
                                    </AlertDescription>
                                </Alert>
                            )}
                        </CardBody>
                    </Card>

                    {/* Onboarded people + their state */}
                    <Card bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px">
                        <CardHeader pb={2}>
                            <HStack justify="space-between">
                                <Heading size="sm" color={textPrimary}>Onboarded people</Heading>
                                <Badge colorScheme="blue">{onboarded.length}</Badge>
                            </HStack>
                        </CardHeader>
                        <CardBody pt={0}>
                            <Text color={textSecondary} mb={3}>
                                Recently created accounts and where each is up to — whether they've signed in yet.
                            </Text>
                            {onboarded.length === 0 ? (
                                <Text color={textMuted}>No accounts onboarded yet.</Text>
                            ) : (
                                <TableContainer overflowX="auto">
                                    <Table size="sm" variant="simple">
                                        <Thead>
                                            <Tr>
                                                <Th>Name</Th>
                                                <Th>Email / phone</Th>
                                                <Th>Username</Th>
                                                <Th>State</Th>
                                                <Th>Last sign-in</Th>
                                                <Th>Created</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {onboarded.map((p) => (
                                                <Tr key={p.id} data-testid="onboarded-row">
                                                    <Td color={textSecondary}>{[p.fName, p.lName].filter(Boolean).join(" ") || "—"}</Td>
                                                    <Td color={textMuted} fontSize="xs">{p.email || "—"}<br />{p.phoneNumber || ""}</Td>
                                                    <Td color={textMuted} fontSize="xs">{p.username || "—"}</Td>
                                                    <Td>
                                                        <Badge colorScheme={p.state === "Signed in" ? "green" : p.state.startsWith("Invited") ? "yellow" : "gray"}>
                                                            {p.state}
                                                        </Badge>
                                                    </Td>
                                                    <Td color={textMuted} fontSize="xs">{fmtDate(p.lastLoginAt)}{p.lastLoginMethod ? ` · ${p.lastLoginMethod}` : ""}</Td>
                                                    <Td color={textMuted} fontSize="xs">{fmtDate(p.createdAt)}</Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardBody>
                    </Card>

                    {/* Potential duplicates */}
                    <Card bg={cardGradientBg} borderColor={cardBorder} borderWidth="1px">
                        <CardHeader pb={2}>
                            <HStack justify="space-between">
                                <HStack spacing={3}>
                                    <Box as={FiAlertTriangle} color="orange.400" aria-hidden />
                                    <Heading size="sm" color={textPrimary}>Potential duplicate accounts</Heading>
                                </HStack>
                                <Badge colorScheme={clusters.length ? "orange" : "green"}>{clusters.length} found</Badge>
                            </HStack>
                        </CardHeader>
                        <CardBody pt={0}>
                            <Text color={textSecondary} mb={3}>
                                Accounts that share an email or phone — likely the same person split across records
                                (e.g. they signed in by passcode and a second account was created). Keep one and merge the rest.
                            </Text>
                            {dupLoading && !clusters.length ? (
                                <Spinner />
                            ) : clusters.length === 0 ? (
                                <Text color={textMuted}>No duplicates detected. 🎉</Text>
                            ) : (
                                <VStack align="stretch" spacing={4} divider={<Divider />}>
                                    {clusters.map((cluster, ci) => (
                                        <Box key={ci} data-testid="duplicate-cluster">
                                            <Text fontSize="sm" color={textMuted} mb={2}>
                                                Shared {cluster.identifierType}: <strong>{cluster.identifier}</strong>
                                            </Text>
                                            <VStack align="stretch" spacing={2}>
                                                {cluster.accounts.map((acc: any) => (
                                                    <HStack key={acc.id} justify="space-between" data-testid="duplicate-account">
                                                        <Box>
                                                            <Text color={textPrimary} fontSize="sm" fontWeight="medium">
                                                                {[acc.fName, acc.lName].filter(Boolean).join(" ") || "(no name)"}
                                                                {acc.username ? ` · ${acc.username}` : ""}
                                                                {acc.hasPassword ? "" : " · no password"}
                                                            </Text>
                                                            <Text color={textMuted} fontSize="xs">
                                                                {acc.email || "no email"} · {acc.phoneNumber || "no phone"} · last login {fmtDate(acc.lastLoginAt)}
                                                            </Text>
                                                        </Box>
                                                        <HStack>
                                                            <Tooltip
                                                                hasArrow openDelay={300} placement="top" whiteSpace="pre-line" shouldWrapChildren
                                                                label={"Reset this account's password.\n\nWhat happens when you click:\n  1. A new temporary password is generated for this account\n  2. It's sent to this account's email/phone\n  3. Their old password stops working immediately\n  4. The new details also appear above (once)"}
                                                            >
                                                                <Button size="xs" variant="ghost" leftIcon={<FiRefreshCw />} onClick={() => handleReset(acc.id)}>
                                                                    Reset
                                                                </Button>
                                                            </Tooltip>
                                                            <Tooltip
                                                                hasArrow openDelay={300} placement="top" whiteSpace="pre-line" shouldWrapChildren
                                                                label={"Make THIS the surviving account and fold the other(s) in this group into it.\n\nWhat happens when you click:\n  1. You're asked to confirm\n  2. The other records' details, history and credentials move onto this account\n  3. Those other records are closed (so the person has just one account)\n  4. They can then sign in with any of their identifiers and land here\n\nCan't be easily undone."}
                                                            >
                                                                <Button
                                                                    size="xs"
                                                                    colorScheme="orange"
                                                                    isLoading={merging}
                                                                    onClick={() => {
                                                                        const mergeIds = cluster.accounts.filter((a: any) => a.id !== acc.id).map((a: any) => a.id);
                                                                        setMergePlan({ keepId: acc.id, mergeIds, label: `${mergeIds.length} account(s) into ${acc.email || acc.phoneNumber || acc.username || acc.id}` });
                                                                        mergeDialog.onOpen();
                                                                    }}
                                                                >
                                                                    Keep &amp; merge
                                                                </Button>
                                                            </Tooltip>
                                                        </HStack>
                                                    </HStack>
                                                ))}
                                            </VStack>
                                        </Box>
                                    ))}
                                </VStack>
                            )}
                        </CardBody>
                    </Card>
                </VStack>
            </Container>
            <FooterWithFourColumns />

            <AlertDialog isOpen={mergeDialog.isOpen} leastDestructiveRef={cancelRef} onClose={mergeDialog.onClose}>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">Merge accounts</AlertDialogHeader>
                        <AlertDialogBody>
                            Merge {mergePlan?.label}? The other records are closed and their data + credentials move to
                            the kept account. This can't be easily undone.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Tooltip label="Don't merge. Nothing changes — all accounts stay as they are." hasArrow openDelay={300} placement="top">
                                <Button ref={cancelRef} onClick={mergeDialog.onClose}>Cancel</Button>
                            </Tooltip>
                            <Tooltip
                                hasArrow openDelay={300} placement="top" whiteSpace="pre-line"
                                label={"Confirm the merge now.\n\nWhat happens when you click:\n  1. The other record(s) are folded into the account you chose to keep\n  2. Their data, history and credentials transfer to it\n  3. The other record(s) are closed\n  4. This can't be easily undone"}
                            >
                                <Button colorScheme="orange" onClick={confirmMerge} ml={3} isLoading={merging}>Merge</Button>
                            </Tooltip>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default OnboardingPage;
